import React, { useRef, useLayoutEffect, useEffect, useState } from "react"
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Collapse, Container } from "react-bootstrap"
import { ToggleButtonGroup, ToggleButton, Stack } from '@mui/material'
import { Overlay } from "ol"
import { Cluster, Vector as VectorSource } from "ol/source"
import { Vector as VectorLayer } from "ol/layer"
import { Icon, Fill, Text, Style, Stroke, Circle } from "ol/style"
import { ImageStatic } from "ol/source"
import { Image } from "ol/layer"
import { GeoJSON } from "ol/format"
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io"
import { FaShieldAlt } from "react-icons/fa"

import { getBaseMap, getIconStyle, setLayerSource } from "Pages/Portal/SmartDevice/Smartwatch/components/utils"
import AlertIcons from "Pages/Portal/SmartDevice/Smartwatch/components/AlertIcons"

import { apiUrl, webUrl } from "Shared/utils"
import useFetchData from "Shared/Hooks/useFetchData"
import Map from "Shared/Components/Map"

const OverlayContentGroup = ({ label, count, items }) => {
  const [open, setOpen] = useState(true)

  return (<>
    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
      <div>{label}: {count}</div>

      <div style={{ display: "flex", alignItems: "center" }}>
        {open ? (
          <IoIosArrowUp onClick={() => setOpen(false)}/>
        ) : (
          <IoIosArrowDown onClick={() => setOpen(true)}/>
        )}
      </div>
    </div>

    <Collapse in={open}>
      <Stack spacing={1}>
        {items.map((i) => 
          <OverlayContentBody type={i["type"]} info={i["data"]}/>
        )}
      </Stack>
    </Collapse>
  </>)
}

const OverlayContentBody = ({ type, info }) => {
  const { role } = useOutletContext()

  const { t } = useTranslation()

  switch (type) {
    case "camera":
      return (
        <div>
          <div style={{ fontSize: "12px" }}><b>{info["camera_name"]}</b></div>
          <div style={{ fontSize: "12px" }}>Model: {info["model"] ?? "-"}</div>
          <div style={{ fontSize: "12px" }}>S/N: {info["sn"] ?? "-"}</div>
          <div style={{ fontSize: "12px" }}>Status: {info["online"] ? "Online" : "Offline"}</div>
      
          {info["url"] && 
            <Button style={{ fontSize: "12px", padding: "0px" }} variant="link" onClick={() => window.open(info["url"])}>{t("cameras_cameraPage")}</Button>
          }
        </div>
      )

    case "smartlock":
      return (
        <div>
          <div style={{ fontSize: "12px" }}><b>{info["device_name"]}</b></div>
          <div style={{ fontSize: "12px" }}>Battery Level: {info["battery_level"] ?? "-"}</div>
          <div style={{ fontSize: "12px" }}>Status: {info["status"]}</div>
        </div>
      )

    case "csWorker":
      return (
        <div>
          <Stack spacing={1} style={{ overflow: "auto" }}>
            <div>
              <div style={{ fontSize: "12px" }}><b><u>{info["zone_name"]}</u></b> 👷{info["workers"].length}</div>
            </div>

            {info["workers"].length > 0 && <>
              <Stack spacing={2}>
                {info["workers"].map((i) => (
                  <div>
                    <div style={{ fontSize: "12px", display: "flex", alignItems: "center" }}><b>{i["worker_name"]}</b> {i["standby_person"] ? <FaShieldAlt style={{ marginLeft: "5px" }}/> : ""}</div>
                    <div style={{ fontSize: "12px" }}>{t("smartwatch_betteryLevel")}: {i["battery_level"]}%</div>
                    <div style={{ fontSize: "12px" }}>{(role === 'pm' || role === 'admin') ? `${t("smartwatch_bodyTemp")}: ${i["body_temp"]}°C` : t(`smartwatch_bodyTemp_${i["body_temp"]}`)}</div>
                    <div style={{ fontSize: "12px" }}>{(role === 'pm' || role === 'admin') ? `${t("smartwatch_heartRate")}: ${i["heart_rate"]} bpm` : t(`smartwatch_heartRate_${i["heart_rate"]}`)}</div>
                    <div style={{ width: "fit-content" }}>
                      {(Object.entries(i["alerts"]).filter(([k, v]) => v === true).length > 0 === true) && 
                        <AlertIcons data={i["alerts"]} />
                      }
                    </div>
                  </div>
                ))}
              </Stack>
            </>}
          </Stack>
        </div>
      )
      
    case "outdoorWorker":
      return (
        <div>
          <div style={{ fontSize: "12px" }}><b>{info["worker_name"]}</b></div>
          <div style={{ fontSize: "12px" }}>{t("smartwatch_betteryLevel")}: {info["battery_level"]}%</div>
          <div style={{ fontSize: "12px" }}>{(role === 'pm' || role === 'admin') ? `${t("smartwatch_bodyTemp")}: ${info["body_temp"]}°C` : t(`smartwatch_bodyTemp_${info["body_temp"]}`)}</div>
          <div style={{ fontSize: "12px" }}>{(role === 'pm' || role === 'admin') ? `${t("smartwatch_heartRate")}: ${info["heart_rate"]}bpm` :t(`smartwatch_heartRate_${info["heart_rate"]}`)}</div>
          <div style={{ width: "fit-content" }}>
            {(Object.entries(info["alerts"]).filter(([k, v]) => v === true).length > 0 === true) && 
              <AlertIcons data={info["alerts"]} />
            }
          </div>
        </div>
      )
  }
}

const OverlayContent = ({ position, opened }) => {
  if (opened.length === 1) {
    return (
      <div style={{ padding: "10px" }}>
        <OverlayContentBody type={opened[0]["type"]} info={opened[0]["data"]}/>
      </div>
    )
  }
  else {
    const cameras = opened.filter((i) => i["type"] === "camera")
    const locks = opened.filter((i) => i["type"] === "smartlock")
    const csWorker = opened.filter((i) => i["type"] === "csWorker")
    const outdoorWorker = opened.filter((i) => i["type"] === "outdoorWorker")

    const l = [
      cameras.length > 0 && { "label": "Camera", "data": cameras, "count": cameras.length, "color": "#F5DEB3" },
      locks.length > 0 && { "label": "Smart Lock", "data": locks, "count": locks.length, "color": "#99EDC3" },
      csWorker.length > 0 && { "label": "Worker (Confined Space)", "data": csWorker, "count": csWorker.map((i) => i["data"]["workers"]).reduce((accumulator, currentArray) => {
        return accumulator.concat(currentArray)
      }, []).length, "color": "#82CAFF" },
      outdoorWorker.length > 0 && { "label": "Worker (Outdoor)", "data": outdoorWorker, "count": outdoorWorker.length, "color": "#F4BBFF" }
    ].filter((i) => i !== false)

    return (
      <Stack spacing={0} style={{ backgroundColor: "green" }}>
        {l.map((i) => 
          <div style={{ backgroundColor: i["color"], padding: "10px" }}>
            <OverlayContentGroup label={i["label"]} count={i["count"]} items={i["data"]} />
          </div>
        )}
      </Stack>
    )
  }
}

const SitePlan = () => {
  const { setPageLoading, windowSize } = useOutletContext()

  const { send } = useFetchData()
  const { t } = useTranslation()

  const mapRef = useRef()
  const overlayRef = useRef()

  const [opened, setOpened] = useState()
  
  const fetchCameras = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/camera/list`,
      returnType: "json"
    })

    return body.map((i) => ({
      "type": "Feature",
      "id": i["camera_id"],
      "geometry": {
        "type": "Point",
        "coordinates": [i["lon"], i["lat"]]
      },
      "properties": {
        "type": "camera",
        "camera_name": i["name"],
        "online": i["online"],
        "model": i["model"],
        "sn": i["sn"],
        "url": i["url"]
      }
    }))
  }

  const fetchProximity = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/proximity/list`,
      returnType: "json"
    })

    return body["features"].filter((i) => i["properties"]["status"] === true).map((i) => ({
      "type": "Feature",
      "id": i["id"],
      "geometry": i["geometry"],
      "properties": {...{"type": "proximity"}, ...i["properties"]}
    }))
  }

  const fetchSmartlock = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smart_lock/data`,
      returnType: "json"
    })

    return body.map((i) => ({
      "type": "Feature",
      "id": i["device_id"],
      "geometry": {
        "type": "Point",
        "coordinates": [i["coor"]["lon"], i["coor"]["lat"]]
      },
      "properties": {
        "type": "smartlock",
        "device_name": i["device_name"],
        "battery_level": i["battery_level"],
        "status": (i["status"] != null) ? (i["status"] ? t("smartlock_locked") : t("smartlock_opened")) : null,  
      }
    }))
  }

  const fetchCSWorkers = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/latest`,
      params: {
        "type": "confined_space"
      },
      returnType: "json"
    })

    const zones = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/zones`,
      returnType: "json"
    })

    return zones.map((i) => ({
      "type": "Feature",
      "id": i["zone_id"],
      "geometry": {
        "type": "Point",
        "coordinates": [i["marker_lon"], i["marker_lat"]]
      },
      "properties": {
        "type": "csWorker",
        "zone_id": i["zone_id"],
        "zone_name": i["zone_name"],
        "zone_description": i["zone_description"],
        "workers": body["data"].filter((j) => j["zone"] === i["zone_id"])
      }
    })).filter((i) => i["properties"]["workers"].length > 0)
  }

  const fetchOutdoorWorkers = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/latest`,
      params: {
        "type": "outdoor"
      },
      returnType: "json"
    })

    return body["features"].filter((i) => i["properties"]["online"] === true).map((i) => ({
      "type": "Feature",
      "id": i["id"],
      "geometry": i["geometry"],
      "properties": {...{"type": "outdoorWorker"}, ...i["properties"]}
    }))
  }

  const fetchFloorPlan = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/floorplan_meta`,
      returnType: "json"
    })

    body.forEach(async (i) => {
      const blob = await send({
        method: "GET",
        url: `${apiUrl}/smartwatch/floorplan`,
        params: {
          "location": i["location"]
        },
        returnType: "blob"
      })

      mapRef.current.map.addLayer(new Image({
        source: new ImageStatic({
          url: URL.createObjectURL(blob),
          imageExtent: [i["x1"], i["y1"] , i["x2"], i["y2"]]
        }),
        zIndex: 1
      }))
    })
  }

  const fetchGeofencingZones = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/geofencing_zones`,
      returnType: "json"
    })

    switch (body["zone_type"]) {
      case "danger_zone":
        mapRef.current.zoneLayer.setStyle(new Style({
          stroke: new Stroke({ color: 'rgb(255, 0, 0)', width: 3 }),
          fill: new Fill({ color: 'rgba(0, 255, 0, 0.1)' })
        }))

        break
      case "working_zone":
        mapRef.current.zoneLayer.setStyle(new Style({
          stroke: new Stroke({ color: 'rgb(0, 255, 0)', width: 3 }),
          fill: new Fill({ color: 'rgba(0, 255, 0, 0.1)' })
        }))

        break

      default:
        mapRef.current.zoneLayer.setStyle(new Style({
          stroke: new Stroke({ color: 'rgb(255, 247, 0)', width: 3 }),
          fill: new Fill({ color: 'rgba(255, 247, 0, 0.1)' })
        }))
        
        break
    }

    setLayerSource(mapRef.current.zoneLayer, body["zones"])
  }

  const fetchMapExtent = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/map_extent`,
      returnType: "json"
    })

    if (body != null) {
      mapRef.current.map.getView().fit([body["x1"], body["y1"], body["x2"], body["y2"]], mapRef.current.map.getSize())
    }
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await Promise.all([
        fetchFloorPlan(),
        fetchGeofencingZones(),
        fetchMapExtent(),
      ])

      const [cameras, proximity, locks, csWorkers, outdoorWorkers] = await Promise.all([
        fetchCameras(),
        fetchProximity(),
        fetchSmartlock(),
        fetchCSWorkers(),
        fetchOutdoorWorkers(),
      ])

      mapRef.current.vectorSource.clear()
      mapRef.current.vectorSource.addFeatures(new GeoJSON({ featureProjection:"EPSG:3857" }).readFeatures({
        "type": "FeatureCollection",
        "features": [...cameras, ...proximity, ...locks, ...csWorkers, ...outdoorWorkers]
      }))

      setPageLoading(false)
    }

    init()    
  }, [])

  useLayoutEffect(() => {
    const vectorSource = new VectorSource({ })

    const clusterSource = new Cluster({
      distance: 20,
      source: vectorSource
    })

    const labelLayer = new VectorLayer({
      source: clusterSource,
      style: function (feature) { 
        const features = feature.get("features")
        const size = features.length

        const cameraFeatures = features.filter((i) => i.get("type") === "camera")
        const lockFeatures = features.filter((i) => i.get("type") === "smartlock")
        const csWorkerFeatures = features.filter((i) => i.get("type") === "csWorker")
        const outdoorWorkerFeatures = features.filter((i) => i.get("type") === "outdoorWorker")
        const proximityFeatures = features.filter((i) => i.get("type") === "proximity")
        
        const size2 = 
          cameraFeatures.length + lockFeatures.length + outdoorWorkerFeatures.length + proximityFeatures.length +
          csWorkerFeatures.map((i) => i.get("workers")).reduce((accumulator, currentArray) => {
            return accumulator.concat(currentArray)
          }, []).length

        if (size > 1) {
          return [
            new Style({
              image: new Circle({
                radius: 20,
                fill: new Fill({ color: '#EE9900' }),
                stroke: new Stroke({ color: '#FFF', width: 3 }),
              }),
              text: new Text({
                text: size2.toString(),
                font: "bold 16px sans-serif",
                fill: new Fill({ color: "#FFF" }),
                stroke: new Stroke({ color: "#000", width: 2 }),
              })
            })
          ]
        }
        else {
          const labelFeature = features[0]

          if (labelFeature.get("type") === "camera") {
            return [
              getIconStyle("cctv", 30, 10)
            ]
          }
          if (labelFeature.get("type") === "proximity") {
            const online = labelFeature.get("status")
            const icon = `${labelFeature.get("icon_type")}_${online ? "online" : "offline"}`
            
            return [
              getIconStyle(icon, 30, 10)
            ]
          }
          else if (labelFeature.get("type") === "smartlock") {
            return [
              getIconStyle("lock", 30, 10)
            ]
          }
          else if (labelFeature.get("type") === "csWorker") {
            return [
              new Style({
                image: new Icon({
                  src: `${webUrl}/icons/cs_worker.png`,
                  width: 30,
                  height: 30
                }),
                text: new Text({
                  text: labelFeature.get("workers").length.toString(),
                  offsetX: 10,
                  offsetY: -10,
                  font: "bold 16px sans-serif",
                  fill: new Fill({ color: "#FFF" }),
                  stroke: new Stroke({ color: "#000", width: 2 }),
                  padding: [5, 5, 5, 5]
                }),
                zIndex: 100
              })          
            ]
          }
          else if (labelFeature.get("type") === "outdoorWorker") {
            return [
              getIconStyle("outdoor_worker", 30, 10)
            ]
          }
        }
      },
      zIndex: 100
    })

    labelLayer.set('name', 'labelLayer')

    const zoneLayer = new VectorLayer({
      source: new VectorSource({}),
      style: function (feature) {
        return new Style({
          stroke: new Stroke({ color: 'red', width: 3 }),
          fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' })
        })
      }
    })

    zoneLayer.set('name', 'zoneLayer')

    const map = getBaseMap([labelLayer, zoneLayer])

    const overlay = new Overlay({
      element: overlayRef.current,
      offset: [25, 0],
    })

    map.addOverlay(overlay)

    const fomatData = (f) => {
      switch (f.get("type")) {
        case "camera":
          return {
            "camera_name": f.get("camera_name"),
            "online": f.get("online"),
            "model": f.get("model"),
            "sn": f.get("sn"),
            "url": f.get("url")
          }
        case "smartlock":
          return {
            "device_name": f.get("device_name"),
            "battery_level": f.get("battery_level"),
            "status": f.get("status"),
          }
        case "csWorker":
          return {
          "zone_name": f.get("zone_name"),
          "zone_description": f.get("zone_description"),
          "workers": f.get("workers"),
          "alerts":  f.get("alerts")
        }
        case "outdoorWorker":
          return {
            "worker_name": f.get("worker_name"),
            "battery_level": f.get("battery_level"),
            "body_temp": f.get("body_temp"),
            "heart_rate": f.get("heart_rate"),
            "alerts":  f.get("alerts")
          }
      }
    }

    map.on("click", (evt) => {
      const found = map.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
        const features = feature.get("features")

        if (features && features.length === 1) {
          const f = features[0]

          const coor = f.getGeometry().getCoordinates()

          setOpened([{
            "type": f.get("type"),
            "data": fomatData(f)
          }])

          overlay.setPosition(coor)

          return true
        }
        else if (features && features.length > 1) {
          const coor = feature.getGeometry().getCoordinates()

          setOpened(features.map((i) => ({
            "type": i.get("type"),
            "data": fomatData(i)
          })))

          overlay.setPosition(coor)

          return true
        }
      } , {
        layerFilter: function (layer) {
          return layer.get('name') === "labelLayer"
        }
      })

      if (!found) {
        resetPopup()
        return
      }
    })

    mapRef.current = {
      "map": map,
      "vectorSource": vectorSource,
      "zoneLayer": zoneLayer,
      "overlay": overlay
    }
  }, [])

  const resetPopup = () => {
    mapRef.current.overlay.setPosition([0, 0])
    setOpened()
  }

  return (<>
    <Container fluid className="mainContainer" style={{ backgroundColor: "white", borderRadius: "15px" }}>
      <Map mapRef={mapRef}/>

      <div ref={overlayRef} className="popup">
        {opened && <div style={{ overflow: "auto", maxHeight: windowSize["height"]*0.6 }}>
          <OverlayContent opened={opened}/>
        </div>}
      </div>
    </Container>
  </>)
}

export default SitePlan
